import Popover from '@mui/material/Popover';
import InfoIcon from '@mui/icons-material/InfoOutlined';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Divider,
  CircularProgress,
  Typography,
} from '@mui/material';
import { getPgtResultsRecipients } from './pgtaHelper';
import type { PgtResultsRecipient as ApiPgtResultsRecipient } from './pgtaHelper';
import { usePatientContext } from '../patinetProvider';


type PgtResultsRecipient = Omit<ApiPgtResultsRecipient, 'pgt_results'> & {
  embryo_no?: string;
  pgt_order?: {
    pgt_order_date?: string;
    pgt_order_time?: string;
  };
  grade_label?: string;
  specimen?: string;
  incubator?: string;
  witness_name?: string;
  embryologist_name?: string;
  ai_grade?: string;
  ai_score?: number | string;
  pgt_results?: {
    kyrotype?: string;
    status?: string;
    gender?: string;
  };
  rank?: string;
};

export default function PgtResult() {
  const { t: tGeneral } = useTranslation('general');
  const { t: tPGT } = useTranslation('pgt_config');
  const theme = useTheme();
  const { patientId } = usePatientContext();
  const [pgtResults, setPgtResults] = React.useState<PgtResultsRecipient[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [popoverData, setPopoverData] = React.useState<any>(null);

  useEffect(() => {
    if (patientId) {
      setLoading(true);
      setError(null);
      getPgtResultsRecipients(patientId)
        .then((response) => {
          // Map and sanitize pgt_results as before
          const mappedResults = (response || []).map((item) => {
            let safePgtResults: { kyrotype?: string; status?: string; gender?: string } | undefined = undefined;
            if (item.pgt_results && typeof item.pgt_results === 'object' && item.pgt_results !== null) {
              const pr = item.pgt_results as Record<string, unknown>;
              safePgtResults = {
                kyrotype: typeof pr.kyrotype === 'string' ? pr.kyrotype : undefined,
                status: typeof pr.status === 'string' ? pr.status : undefined,
                gender: typeof pr.gender === 'string' ? pr.gender : undefined,
              };
            }
            return {
              ...item,
              pgt_results: safePgtResults,
            };
          });

          // Add rank calculation based on ai_score (highest = #1)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const withScore = mappedResults.map((o: any, idx: number) => ({
            ...o,
            _ai_score_num: o.ai_score !== undefined && o.ai_score !== null && o.ai_score !== '' ? Number(o.ai_score) : null,
            _originalIndex: idx,
          }));
          const sorted = [...withScore].sort((a, b) => {
            if (a._ai_score_num === null && b._ai_score_num === null) return 0;
            if (a._ai_score_num === null) return 1;
            if (b._ai_score_num === null) return -1;
            return b._ai_score_num - a._ai_score_num;
          });
          const rankMap = new Map();
          let rank = 1;
          for (const o of sorted) {
            if (o._ai_score_num !== null) {
              rankMap.set(o._originalIndex, `#${rank} Euploid Normal`);
              rank++;
            } else {
              rankMap.set(o._originalIndex, '-');
            }
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const withRank = withScore.map((o: any) => {
            const { _originalIndex, ...rest } = o;
            return { ...rest, rank: rankMap.get(_originalIndex) };
          });
          setPgtResults(withRank);
        })
        .catch((error) => {
          setError(tPGT('fetchPgtResultsError', 'Failed to fetch PGT Results. Please try again.'));
          // eslint-disable-next-line no-console
          console.error('Failed to fetch PGT Results:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [patientId,tPGT]);

  
  // const fetchReport = useCallback(async () => {
  //   try {
  //     const response = await fetchViaProxy('/api/v2/requisites/{requisite_id}/file/{file_id}/download', 'GET');
  //     // eslint-disable-next-line no-console
  //     console.log(' Fetched Physicians:', response);
  //   } catch (error) {
  //     // eslint-disable-next-line no-console
  //     console.error('Failed to fetch:', error);
  //   }
  // }, []);

  return (
    <Card sx={{ p: 1, boxShadow: 'none', border: 'none' }}>
      <Divider sx={{ mb: 2, borderColor: theme.palette.divider }} />
      <CardContent sx={{ p: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="error" variant="body1">{error}</Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              boxShadow: 'none',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflowX: 'auto',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    tPGT('embryoNo', 'Embryo ID'),
                    tPGT('biopsyDateTime', 'Biopsy Date'),
                    tGeneral('grade', 'Grade'),
                    tPGT('aiScore', 'AI Score'),
                    tPGT('day', 'Day'),
                    // tPGT('incubator', 'Incubator'),
                    tPGT('Biopsy By', 'Biopsy By'),
                    tPGT('witness', 'Witness'),
                    tPGT('karyotype', 'Karyotype'),
                    tPGT('result', 'Result'),
                    tPGT('gender', 'Gender'),
                    tPGT('rank', 'Rank'),
                  ].map((header, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        fontWeight: 600,
                        fontSize: '0.88rem',
                        color: '#333',
                        whiteSpace: 'nowrap',
                        padding: '10px 16px',
                        borderBottom: '1px solid #ccc',
                        lineHeight: 2.5,
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pgtResults.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      {tPGT('noResultsFound', 'No results found.')}
                    </TableCell>
                  </TableRow>
                ) : (
                  pgtResults.map((item) => (
                    <TableRow
                      key={item.embryo_no ?? item.embryo_number ?? item.id}
                      hover
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.selected,
                          transform: 'scale(1.005)',
                          boxShadow: `0 2px 4px ${theme.palette.action.hover}`,
                        },
                        '& td': {
                          fontSize: '0.85rem',
                          padding: '10px 16px',
                          lineHeight: 2.5,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                      }}
                    >
                      <TableCell>{item.embryo_no ?? item.embryo_number ?? item.id}</TableCell>
                      <TableCell>{item.pgt_order ? `${item.pgt_order.pgt_order_date ?? '-'}` : ' '}</TableCell>
                      <TableCell>{item.grade_label ?? (item.grade ?? '-')}</TableCell>
                      <TableCell>{item.ai_score ?? '-'}</TableCell>
                      <TableCell>{item.day ?? '-'}</TableCell>
                      {/* <TableCell>{item.incubator ?? '-'}</TableCell> */}
                      <TableCell>{item.embryologist_name ?? '-'}</TableCell>
                      <TableCell>{item.witness_name ?? '-'}</TableCell>
                      <TableCell>{item.pgt_results ? item.pgt_results.kyrotype ?? '-' : '-'}</TableCell>
                      <TableCell>
                        <span>
                          {(() => {
                            const r = item.pgt_results;
                            if (!r) return '-';
                            const isDraft = r.status === 'DRAFT';
                            // Dynamically check all fields except 'status' for null/empty/undefined
                            const othersEmpty = Object.keys(r)
                              .filter(k => k !== 'status')
                              .every(k => {
                                const v = r[k as keyof typeof r];
                                return v === undefined || v === null || v === '';
                              });
                            if (isDraft && othersEmpty) return '-';
                            return r.status !== '' ? r.status : '-';
                          })()}
                          {(() => {
                            const r = item.pgt_results;
                            if (!r) return null;
                            const isDraft = r.status === 'DRAFT';
                            const othersEmpty = Object.keys(r)
                              .filter(k => k !== 'status')
                              .every(k => {
                                const v = r[k as keyof typeof r];
                                return v === undefined || v === null || v === '';
                              });
                            if (isDraft && othersEmpty) return null;
                            return (
                              <InfoIcon
                                fontSize="small"
                                style={{ cursor: 'pointer', color: '#EFAE78', marginLeft: 6, verticalAlign: 'middle' }}
                                onClick={(e: React.MouseEvent<HTMLElement | SVGElement>) => {
                                  setAnchorEl(e.currentTarget as unknown as HTMLElement);
                                  setPopoverData(item.pgt_results);
                                }}
                              />
                            );
                          })()}
                        </span>
                        <Popover
                          open={Boolean(anchorEl)}
                          anchorEl={anchorEl}
                          onClose={() => {
                            setAnchorEl(null);
                            setPopoverData(null);
                          }}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          PaperProps={{
                            sx: {
                              borderRadius: 3,
                              boxShadow: '0.2px 0.2px rgba(0,0,0,0.12)',
                              minWidth: 340,
                              maxWidth: 420,
                              background: '#fff',
                              p: 0,
                              border: '1px solid #e0e0e0',
                              fontFamily: 'Montserrat, sans-serif',
                              transition: 'box-shadow 0.2s',
                              '&:hover': {
                                // boxShadow: '1px 2px rgba(0,0,0,0.18)',
                              },
                            },
                          }}
                        >
                          <Box sx={{ p: 2, pb: 1.5 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                mb: 1.2,
                                borderBottom: '1px solid #f0f0f0',
                                pb: 0.7,
                                fontFamily: 'Montserrat, sans-serif',
                                color: '#222',
                                letterSpacing: 0.1,
                              }}
                            >
                              PGT Result Details
                            </Typography>
                            {popoverData ? (
                              <Box>
                                {[{
                                  label: 'Status',
                                  value: popoverData.status,
                                }, {
                                  label: 'Gender',
                                  value: popoverData.gender,
                                }, {
                                  label: 'Karyotype',
                                  value: popoverData.kyrotype,
                                },
                                popoverData.mitosure !== undefined ? {
                                  label: 'Mitosure',
                                  value: popoverData.mitosure,
                                } : null,
                                popoverData['no of cells'] !== undefined ? {
                                  label: 'No of Cells',
                                  value: popoverData['no of cells'],
                                } : null,
                                popoverData.notes !== undefined ? {
                                  label: 'Notes',
                                  value: popoverData.notes,
                                } : null
                                ]
                                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  .filter((v): v is { label: string; value: any } => v !== null)
                                  .map((item, idx) => (
                                    <Box
                                      key={item.label}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        mb: idx === 5 ? 0 : 1.1,
                                        py: 0.2,
                                        borderBottom: idx !== 5 ? '1px solid #f7f7f7' : 'none',
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: 600,
                                          minWidth: 110,
                                          color: '#444',
                                          fontSize: 15,
                                          fontFamily: 'Montserrat, sans-serif',
                                          flexShrink: 0,
                                          pr: 1.5,
                                          pt: 0.1,
                                        }}
                                      >
                                        {item.label}:
                                      </Typography>
                                      <Typography
                                        sx={{
                                          color: '#222',
                                          fontSize: 15,
                                          fontFamily: 'Montserrat, sans-serif',
                                          wordBreak: 'break-word',
                                          whiteSpace: 'pre-line',
                                          flex: 1,
                                          pt: 0.1,
                                        }}
                                      >
                                        {item.value || '-'}
                                      </Typography>
                                    </Box>
                                  ))}
                              </Box>
                            ) : null}
                          </Box>
                        </Popover>
                      </TableCell>
                      <TableCell>{item.pgt_results ? item.pgt_results.gender ?? '-' : '-'}</TableCell>
                      <TableCell>{item.rank ?? '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button variant="contained" color="secondary" sx={{ px: 4, borderRadius: 3 }}>
            {tPGT('generateReport', 'Generate Report')}
          </Button>
        </Box> */}
      </CardContent>
    </Card>
  );
}
